<template>
  <!-- main container -->
  <div class="modals-manager" :class="{ hidden: openedModal == null }">
    <!-- modal for new stream adding -->
    <add-stream-modal
      :show="openedModal == modals.ADD"
      :loading="addStreamModal.isLoading"
      :clear="addStreamModal.isCleared"
      @addStream="handleAddStream($event)"
      @close="closeAdminModal"
    />
    <!-- modal for stream editing -->
    <edit-stream-modal
      :show="openedModal == modals.EDIT"
      :loading="editStreamModal.isLoading"
      :stream="editingStream"
      @editStream="handleEditStream($event)"
      @close="closeAdminModal"
    />
    <!-- modal to remove stream -->
    <remove-stream-modal
      :show="openedModal == modals.REMOVE"
      :loading="removeStreamModal.isLoading"
      :streams="removingStreams"
      :start-stream-id="removingStartId"
      @removeStream="handleRemoveStream($event.id)"
      @close="closeAdminModal"
    />
    <!-- modal with stream information -->
    <stream-info-modal
      :show="openedModal == modals.INFO"
      :stream-info="streamInfo"
      @close="closeAdminModal"
    />
    <source-info-modal
      :show="openedModal == modals.SOURCE"
      :stream-name="verificationSreamInfo"
      :flag-server="flagServer"
      @shangeFlagUpdateServer="$emit('shangeFlagUpdateServer')"
      @close="closeAdminModal"
    />
    <recorder-info-modal
      :show="openedModal == modals.RECORDER"
      :stream-name="verificationSreamInfo"
      :flag-server="flagServer"
      @shangeFlagUpdateServer="$emit('shangeFlagUpdateServer')"
      @close="closeAdminModal"
    />
  </div>
</template>

<script>
/** Modals */
import AddStreamModal from "@/components/admin/modals/AddStreamModal.vue";
import EditStreamModal from "@/components/admin/modals/EditStreamModal.vue";
import RemoveStreamModal from "@/components/admin/modals/RemoveStreamModal.vue";
import StreamInfoModal from "@/components/admin/modals/StreamInfoModal.vue";
import SourceInfoModal from "@/components/admin/modals/SourceInfoModal.vue";
import RecorderInfoModal from "@/components/admin/modals/RecorderInfoModal.vue";

/** Vuex methods */
import { mapState, mapActions } from "vuex";

// @group Dialog
// Manager, that controls showing/closing of admin modals
export default {
  name: "AdminModalsManager",
  components: {
    AddStreamModal,
    EditStreamModal,
    RemoveStreamModal,
    StreamInfoModal,
    SourceInfoModal,
    RecorderInfoModal
  },
  data() {
    return {
      /** modals parameters */
      addStreamModal: {
        isLoading: false,
        /** set to true, to clear modal fields */
        isCleared: true
      },
      editStreamModal: {
        isLoading: false
      },
      removeStreamModal: {
        isLoading: false
      },
      sourceInfoModal: {
        isLoading: true
      }
    };
  },
  computed: {
    ...mapState({
      modals: state => state.modals.admin.MODALS,
      modalData: state => state.modals.admin.modalData,
      openedModal: state => state.modals.admin.opened
    }),
    // @vuese
    // Stream data object or null
    editingStream() {
      return this.modalData?.stream;
    },
    // @vuese
    // Streams to remove list or empty array
    removingStreams() {
      return this.modalData?.streams ?? [];
    },
    // @vuese
    // Stream to remove ID or null
    removingStartId() {
      return this.modalData?.startId;
    },
    // @vuese
    // Stream data object or null
    streamInfo() {
      return this.modalData?.stream;
    },
    verificationSreamInfo() {
      return this.streamInfo?.streamName;
    }
  },
  props: {
    flagServer: {
      default: null,
      type: Boolean
    }
  },
  watch: {
    // @vuese
    // Show opened modal
    openedModal() {
      switch (this.openedModal) {
        case this.modals.ADD:
          /** set clear flag to 'false', so we will can erase modal by setting 'true' */
          this.addStreamModal.isCleared = false;
      }
    }
  },
  methods: {
    ...mapActions("admin", ["addStream", "editStream", "removeStream"]),
    ...mapActions("modals", ["closeAdminModal"]),
    // @vuese
    // Create and save new stream
    // @arg data - stream data
    async handleAddStream(data) {
      /** start loading */
      this.addStreamModal.isLoading = true;

      try {
        const fullData = {
          /** get server information, that was set previously */
          ...this.modalData,
          /** if server info will be in 'data' it will replace info from 'addStreamData' */
          ...data
        };

        await this.addStream(fullData);

        /** success message */
        this.showSuccess(
          "Stream Added",
          `Stream <${fullData.streamName}> added to server ${fullData.serverName}`
        );

        /** close modal */
        this.closeAdminModal();
        /** set modal parameters to default values */
        this.addStreamModal.isCleared = true;
      } catch (e) {
        /** error handling */
        this.showError("Stream Adding Failed", e);
      }

      /** finish loading */
      this.addStreamModal.isLoading = false;
    },
    // @vuese
    // Edit existing stream parameters
    // @arg changedData - new & old stream data (changed url, id, server id)
    async handleEditStream(changedData) {
      /** start loading */
      this.editStreamModal.isLoading = true;
      try {
        const newData = {
          streamName : this.editingStream.streamName,
          /** origin stream data, that contains all stream information */
          // ...this.editingStream,
          /** changed data after original data, so new params will replace old */
          ...changedData
        };

        /** API call and local data changes */
        await this.editStream(newData);

        /** show notification for user, if all was fine */
        this.showSuccess("Stream Edited", `New URL - ${newData.streamUrl}`);

        /** close modal */
        this.closeAdminModal();
      } catch (e) {
        /** show error message for user, if something went wrong */
        this.showError("Stream Editing Failed", e);
      }

      /** finish loading */
      this.editStreamModal.isLoading = false;
    },
    // @vuese
    // Remove stream and close modal
    // @arg id - stream ID
    async handleRemoveStream(id) {
      /** start loading */
      this.removeStreamModal.isLoading = true;

      try {
        await this.removeStream(id);

        /** success message */
        this.showSuccess("Stream Removed", "");

        /** close modal */
        this.closeAdminModal();
      } catch (e) {
        /** error handling */
        this.showError("Stream Removing Failed", e);
      }
      /** finish loading */
      this.removeStreamModal.isLoading = false;
    },
    // @vuese
    // Show error notification
    // @arg title - title text <br>
    // @arg text - message text
    showError(title, text) {
      console.log(text);
      this.$notify({
        group: "foo",
        type: "error",
        title: title,
        text: text
      });
    },
    // @vuese
    // Show success notification
    // @arg title - title text <br>
    // @arg text - message text
    showSuccess(title, text) {
      this.$notify({
        group: "foo",
        type: "success",
        title: title,
        text: text
      });
    }
  }
};
</script>

<style scoped>
/** modals manager covers over display */
.modals-manager {
  position: absolute;
  width: 100%;
  height: 100%;
}

/** modals layer is fully transparent (for interconnections) when no modal's showing
otherwise it would block every click on page */
.modals-manager.hidden {
  pointer-events: none;
}
</style>
